import type { App } from 'vue'
import { ApiError } from '@/shared/api/http/types'
import { IS_DEV } from '@/shared/config'

export interface ErrorHandlerOptions {
  onError?: (error: Error, context: string) => void
  logErrors?: boolean
}

const defaultOptions: ErrorHandlerOptions = {
  logErrors: IS_DEV,
}

export function setupErrorHandler(app: App, options: ErrorHandlerOptions = {}) {
  const { onError, logErrors } = { ...defaultOptions, ...options }

  // Vue component errors
  app.config.errorHandler = (err, instance, info) => {
    const error = err instanceof Error ? err : new Error(String(err))
    const componentName = instance?.$options.name || instance?.$options.__name || 'Anonymous'
    const context = `Vue [${componentName}]: ${info}`

    if (logErrors) {
      console.error(`[Error] ${context}\n\n`, error)
    }

    onError?.(error, context)
  }

  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason))
    const context = 'Unhandled Promise Rejection'

    if (logErrors) {
      console.error(`[Error] ${context}`, error)
    }

    onError?.(error, context)
  })
}

/**
 * Extract user-friendly message from various error types
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message
  }
  if (error instanceof Error) {
    return error.message
  }
  return 'An unexpected error occurred'
}
